/**
 * "Delete story — soft delete; a real confirmation dialog, not `window.confirm`".
 *
 * The checkbox names the mechanism it is replacing, so it is worth saying what is wrong with
 * `window.confirm` beyond taste: it cannot name what it is deleting in any way the player can
 * check (*"Are you sure?"*), it blocks the whole page, it is unstyleable, and on
 * mobile Safari it can be suppressed entirely — at which point the delete either always
 * happens or never does, depending on the return value nobody checked.
 *
 * What this adds, all of it cheap:
 *
 * - **It names the character and the length of the run**, which is the only information that
 * distinguishes two stories in a list of stories.
 * - **Cancel is the default focus and the safe key.** Escape cancels; the destructive control
 * is a deliberate reach, and it carries `danger` rather than looking like *cancel*.
 * - **The failure has somewhere to go.** A delete that 404s or 401s leaves the dialog open
 * with the reason in it, rather than closing as if it had worked.
 *
 * "Soft delete" is the server's business (`StoryStatus.deleted`, which is what makes
 * `max_stories_allowed` countable) and is deliberately **not** surfaced here: to the player
 * it is gone, and offering an undo we do not implement would be a lie about a restore path.
 *
 * The confirmation body is a **child component that is mounted only while a story is being
 * deleted**, rather than one that syncs itself to a prop in an effect. Reopening the dialog
 * on a different story is then a fresh mount with fresh state, so the last attempt's failure
 * message cannot survive into it — the same reason the turn store resets on `begin`.
 */
import { useEffect, useRef, useState } from 'react';
import { describeApiError } from '@/api/errorMessages';
import { useDeleteStory } from '@/api/queries';
import type { StorySummary } from '@/api/generated';
import { useT } from '@/i18n/context';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { toastOk } from '@/ui/toast';

interface DeleteStoryDialogProps {
  /** The story to delete, or null when the dialog is shut. */
  story: StorySummary | null;
  onClose: () => void;
}

export function DeleteStoryDialog({ story, onClose }: DeleteStoryDialogProps) {
  return story ? <Confirm story={story} onClose={onClose} /> : null;
}

function Confirm({ story, onClose }: { story: StorySummary; onClose: () => void }) {
  const t = useT();
  const remove = useDeleteStory();
  const [failure, setFailure] = useState<string | null>(null);
  const cancel = useRef<HTMLButtonElement | null>(null);

  // Focus the safe control, not the destructive one. `Dialog` focuses the panel so the whole
  // dialog is announced first; this moves focus one step further, to the way out.
  useEffect(() => {
    cancel.current?.focus();
  }, []);

  const confirm = (): void => {
    remove.mutate(story.id, {
      onSuccess: () => {
        toastOk(t('ui.story.deleted', { name: story.character_name }));
        onClose();
      },
      onError: (error: unknown) => {
        setFailure(describeApiError(t, error));
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ui.story.delete_title')}
      footer={
        <>
          <Button ref={cancel} variant="quiet" size="action" onClick={onClose}>
            {t('ui.common.cancel')}
          </Button>
          <Button variant="danger" size="action" disabled={remove.isPending} onClick={confirm}>
            {remove.isPending ? t('ui.story.deleting') : t('ui.story.delete_forever')}
          </Button>
        </>
      }
    >
      <p className="text-fg" style={{ fontSize: 'var(--text-action)', lineHeight: 1.5 }}>
        {t('ui.story.delete_confirm', { name: story.character_name })}
      </p>
      <p className="text-fg-faint mt-2 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {t.plural('ui.story.chapters', story.chapter_count)}
        {' · '}
        {story.status === 'dead' ? t('ui.story.delete_finished') : t('ui.story.delete_alive')}
      </p>
      {failure ? (
        <p
          role="alert"
          className="mt-4 text-[color:var(--color-ramp-danger)]"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {failure}
        </p>
      ) : null}
    </Dialog>
  );
}
